import { Modal, ModalBody, ModalFooter, Box, Autocomplete, Button, Alert } from '@coolblue-development/freddy';
import { useState } from 'react';
import { Queue } from '../../../../../models/Queue';
import { useTransferQueues } from '../../../hooks/useTransferQueues';
import { useEmailContext } from '../../../../../contexts/EmailContext';
import { fetchContact } from '../../../../../utils/fetchContact';

interface TransferEmailModalProps {
  open: boolean;
  onClose: () => void;
  onTransfer: (queue: Queue) => void;
}

const AUTOCOMPLETE_TRANSLATIONS = {
  labelChipsCollapsed: '',
  labelLoading: 'Loading...',
  labelNoOptions: 'No queues found',
  labelSelectAll: 'Select all',
  ariaLabelOpenDropdown: 'Open dropdown',
  ariaLabelCloseDropdown: 'Close dropdown',
  ariaLabelRemoveChip: 'Remove',
  ariaLabelRemoveAllChips: 'Remove all'
};

const TransferEmailModal = ({ open, onClose, onTransfer }: TransferEmailModalProps): JSX.Element => {
  const { selectedEmail } = useEmailContext();
  const contact = selectedEmail ? fetchContact(selectedEmail) : null;
  const originalQueueId = contact?.getQueue?.()?.queueId;
  const { queues, isLoading, hasError } = useTransferQueues(open, originalQueueId);
  const [destination, setDestination] = useState<Queue | null>(null);

  const handleClose = (): void => {
    setDestination(null);
    onClose();
  };

  const handleTransfer = (): void => {
    if (!destination) return;
    onTransfer(destination);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      loading={isLoading}
      title="Transfer email"
      size="large"
      translations={{ ariaLabelClose: 'Close' }}
      sx={{ overflow: 'visible' }}
      body={
        <ModalBody>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Autocomplete<Queue>
              options={queues}
              value={destination}
              onChange={(value: Queue | null) => setDestination(value)}
              getOptionLabel={(option: Queue | Record<string, unknown>) => (option as Queue).name}
              getOptionKey={(option: Queue | Record<string, unknown>) => (option as Queue).id}
              label="Transfer to queue"
              placeholder="Select a queue"
              loading={isLoading}
              translations={AUTOCOMPLETE_TRANSLATIONS}
            />

            {hasError && <Alert intent="error" description="Failed to load queues. Please close and try again." />}
          </Box>
        </ModalBody>
      }
      footer={
        <ModalFooter>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button onClick={handleClose} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleTransfer} disabled={!destination || isLoading} variant="primary">
              Transfer email
            </Button>
          </Box>
        </ModalFooter>
      }
    />
  );
};

export default TransferEmailModal;
